import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { makeObservable, computed, runInAction, observable, reaction } from "mobx";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { getJSON, putJSON } from "@whatslly/commons/apiUtils";
import { parseOrReturnError } from "./utils";
import { OrgConfigs } from "@whatslly/types/OrgConfigs";
import type { PermissionInstanceConfig, PermissionInstanceConfigs } from "@whatslly/commons/platformPermissions";
import { getInstanceConfigsFromOrgConfigs, INSTANCE_CONFIGS } from "@whatslly/commons/platformPermissions";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { SaveOrganizationConfigsMetadata, SaveOrganizationConfigsResponse } from "@whatslly/types/SaveOrganizationConfigs";
import type { GetTuvisOrganizationConfigsResponseType } from "@whatslly/types/GetTuvisOrganizationConfigs";
import { ChunkedQueue } from "@whatslly/commons/chunkedQueue";
import groupBy from "lodash/groupBy";
import { UrlStore } from "./urlStore";

interface ConfigsState<T extends keyof OrgConfigs = keyof OrgConfigs> {
  data: Partial<OrgConfigs> | null;
  promises: PartialRecord<T, IPromiseBasedObservable<OrgConfigs[T] | undefined>>;
  error: string | null;
}

interface ConfigRequest {
  keys: (keyof OrgConfigs)[];
  orgId: string;
  resolve: (configs: Partial<OrgConfigs>) => void;
  reject: (error: Error) => void;
}

@injectable()
export class OrgConfigsStore {
  static BATCH_SIZE = 5;
  static BATCH_INTERVAL = 10;

  private _configsState: Record<string, ConfigsState> = {};
  private _lastOrgId: string | undefined = undefined;
  private _configQueue: ChunkedQueue<ConfigRequest>;

  constructor(
    @inject(InjectUserStore) private userStore: IUserStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    this._configQueue = new ChunkedQueue<ConfigRequest>(async (requests) => this.processBatchedRequests(requests), {
      chunkSize: OrgConfigsStore.BATCH_SIZE,
      interval: OrgConfigsStore.BATCH_INTERVAL,
    });

    makeObservable<OrgConfigsStore, "_configsState" | "currentOrgId" | "configs" | "error" | "isLoaded">(this, {
      _configsState: observable,
      currentOrgId: computed,
      configs: computed,
      error: computed,
      isLoaded: computed,
      configsState: computed,
    });

    reaction(
      () => [this.userStore.initialized, this.userStore.refreshes, this.currentOrgId],
      () => this.reset(),
      { fireImmediately: true }
    );
  }

  get currentOrgId(): string | undefined {
    return this.urlStore.searchParams?.get("orgId") || this.userStore.user?.organizationId;
  }

  get configsState(): ConfigsState {
    const orgId = this.currentOrgId;
    if (!orgId) return this.defaultConfigsState;

    return this.getOrCreateConfigsState(orgId);
  }

  set configsState(state: ConfigsState) {
    const orgId = this.currentOrgId;
    if (!orgId) return;

    this._configsState[orgId] = this.cloneConfigsState(state);
  }

  get configs(): Partial<OrgConfigs> | null {
    return this.configsState.data ?? null;
  }

  get orgDomainConfigs(): PermissionInstanceConfigs {
    if (!this.configs) return {};
    return getInstanceConfigsFromOrgConfigs(this.configs);
  }

  get areOrgConfigsLoading(): boolean {
    return Object.keys(this.orgDomainConfigs).some((key) => this.getConfigPromise(key as keyof OrgConfigs));
  }

  get error(): string | null {
    return this.configsState.error ?? null;
  }

  get isLoaded(): boolean {
    return this.configsState.error === null;
  }

  isLoading(keys: readonly (keyof OrgConfigs)[]): boolean {
    return keys.some((key) => !this.getConfigPromise(key));
  }

  getConfigs<T extends keyof OrgConfigs>(keys: readonly T[]): Pick<OrgConfigs, T> {
    const state = this.configsState;
    const missingKeys = keys.filter((key) => !this.hasConfig(key) && !this.getConfigPromise(key));
    if (missingKeys.length > 0) {
      this.getObservableConfigs(missingKeys);
    }
    return this.parseConfigs(keys, state.data);
  }

  getConfig<T extends keyof OrgConfigs>(key: T): OrgConfigs[T] {
    return this.getConfigs([key])[key];
  }

  getObservableConfigs<T extends keyof OrgConfigs>(keys: readonly T[]): IPromiseBasedObservable<Pick<OrgConfigs, T>> {
    const missingKeys = keys.filter((key) => !this.hasConfig(key));
    const state = this.configsState;
    const orgId = this.currentOrgId;

    if (missingKeys.length > 0 && orgId) {
      const configsNotLoaded = missingKeys.filter((key) => !this.getConfigPromise(key));
      if (configsNotLoaded.length > 0) {
        const batchedPromise = new Promise<Partial<OrgConfigs>>((resolve, reject) => {
          this._configQueue.enqueue({
            keys: configsNotLoaded,
            orgId,
            resolve,
            reject,
          });
        });

        for (const key of configsNotLoaded) {
          state.promises[key] = fromPromise(
            batchedPromise.then((configs) => {
              const currentState = this.getOrCreateConfigsState(orgId);
              if (currentState.data?.[key] === undefined) {
                this.setConfigs({ [key]: configs[key] ?? null }, orgId);
              }
              delete currentState.promises[key];
              return currentState.data?.[key];
            })
          );
        }
      }

      return fromPromise(
        Promise.all(missingKeys.map((key) => this.getConfigPromise(key)!)).then(() => {
          return this.parseConfigs(keys, this.getOrCreateConfigsState(orgId).data);
        })
      );
    }

    return fromPromise(Promise.resolve(this.parseConfigs(keys, state.data)));
  }

  getObservableConfig<T extends keyof OrgConfigs>(key: T): IPromiseBasedObservable<OrgConfigs[T]> {
    return fromPromise(this.getObservableConfigs([key]).then((configs) => configs[key] ?? undefined));
  }

  async reloadAllConfigs(): Promise<Partial<OrgConfigs> | null> {
    const currentOrgId = this.currentOrgId;
    this.userStore.instanceConfigs = null;
    return this.loadDefaultConfigs().then((configs) => {
      const configsWithNoValue = Object.entries(configs).filter(([, value]) => value === undefined);
      if (configsWithNoValue.length === 0) {
        if (currentOrgId === this.currentOrgId) {
          this.userStore.instanceConfigs = configs;
        }
      }

      return configs;
    });
  }

  async loadConfigs(keys?: (keyof OrgConfigs)[], options?: { orgId?: string }): Promise<GetTuvisOrganizationConfigsResponseType> {
    const currentOrgId = options?.orgId ?? this.currentOrgId;
    try {
      if (!currentOrgId || !this.userStore || !this.userStore.etoken) {
        throw new Error("No orgId or etoken");
      }

      const urlParams = new URLSearchParams();
      if (keys) {
        keys.forEach((key) => urlParams.append("configKeys", key));
      }
      const urlParamsString = keys ? `?${urlParams.toString()}` : "";
      const res = await getJSON({
        urlSuffix: `/integrations/organizations/${currentOrgId}/configs${urlParamsString}`,
        etoken: this.userStore.etoken ?? "",
        pod: import.meta.env.VITE_API_DEFAULT_POD,
      }).then((r) => parseOrReturnError<GetTuvisOrganizationConfigsResponseType>(r, this.logger));
      runInAction(() => {
        if (res.success && res.configs) {
          this.setConfigs(res.configs, currentOrgId);
        }
      });

      return res;
    } catch (error) {
      runInAction(() => {
        this.setError(error instanceof Error ? error.message : "Unknown error occurred", currentOrgId);
      });
      return { success: false, error: error instanceof Error ? error.message : "Unknown error occurred" };
    }
  }

  async saveConfig<T extends keyof OrgConfigs>(key: T, value: OrgConfigs[T]) {
    return this.saveConfigs({ [key]: value });
  }

  async saveConfigs(configs: Partial<OrgConfigs>, metadata?: SaveOrganizationConfigsMetadata, reason?: string, whoRequested?: string) {
    const orgId = this.currentOrgId;
    if (!orgId) return { success: false, error: "No orgId" };

    const res = await putJSON({
      urlSuffix: "/integrations/organizations/configs",
      etoken: this.userStore.etoken ?? "",
      pod: import.meta.env.VITE_API_DEFAULT_POD,
      body: JSON.stringify({ orgId, configs, metaData: metadata, reason, whoRequested }),
    }).then((r) => parseOrReturnError<SaveOrganizationConfigsResponse>(r, this.logger));

    if (res.success) {
      this.setConfigs(configs, orgId);
      const instanceConfigsKeys = INSTANCE_CONFIGS;
      const rootConfigKeys = Object.keys(configs).filter((key) => instanceConfigsKeys.includes(key as PermissionInstanceConfig));
      const rootConfigs = this.parseConfigs(rootConfigKeys as (keyof OrgConfigs)[], configs);
      if (Object.keys(rootConfigs).length > 0) {
        this.userStore.instanceConfigs = { ...this.userStore.instanceConfigs, ...rootConfigs };
      }
    }

    return res;
  }

  hasConfig<T extends keyof OrgConfigs>(key: T): boolean {
    return this.configsState.data != null && this.configsState.data[key] !== undefined;
  }

  private getConfigPromise<T extends keyof OrgConfigs>(key: T) {
    return this.configsState.promises[key] ?? null;
  }

  private setError(error: string | null, orgId?: string) {
    const targetOrgId = orgId ?? this.currentOrgId;
    if (!targetOrgId) return;

    this.getOrCreateConfigsState(targetOrgId).error = error;
  }

  private setConfigs(data: Partial<OrgConfigs>, orgId: string) {
    const state = this.getOrCreateConfigsState(orgId);
    state.data = { ...state.data, ...data };
    state.error = null;
  }

  private loadDefaultConfigs() {
    return this.getObservableConfigs(INSTANCE_CONFIGS);
  }

  private parseConfigs<T extends keyof OrgConfigs>(keys: readonly T[], configs: Partial<OrgConfigs> | null): Pick<OrgConfigs, T> {
    return keys.reduce(
      (acc, key) => {
        acc[key] = configs?.[key];
        return acc;
      },
      {} as Pick<OrgConfigs, T>
    );
  }

  private reset() {
    if (!this.currentOrgId || !this.userStore?.etoken) return;
    if (this.currentOrgId === this._lastOrgId && this.userStore.user?.organizationId !== this.currentOrgId) return;
    if (this.currentOrgId === this._lastOrgId) this.configsState = this.defaultConfigsState;
    this.reloadAllConfigs();
    this._lastOrgId = this.currentOrgId;
  }

  private async processBatchedRequests(requests: ConfigRequest[]): Promise<void> {
    const requestsByOrgId = groupBy(requests, "orgId");
    for (const [orgId, orgRequests] of Object.entries(requestsByOrgId)) {
      try {
        const allKeys = [...new Set(orgRequests.flatMap((req) => req.keys))];
        const result = await this.loadConfigs(allKeys, { orgId });

        if (result.success && result.configs) {
          orgRequests.forEach((request) => {
            const requestedConfigs: Partial<OrgConfigs> = {};
            request.keys.forEach((key) => {
              const configValue = result.configs[key];
              if (configValue !== undefined) {
                Object.assign(requestedConfigs, { [key]: configValue });
              }
            });

            request.resolve(requestedConfigs);
          });
        } else {
          const errorMessage = result.success === false ? result.error : "Failed to load configs";
          const error = new Error(errorMessage);
          orgRequests.forEach((request) => request.reject(error));
        }
      } catch (error) {
        const err = error instanceof Error ? error : new Error("Unknown error occurred");
        orgRequests.forEach((request) => request.reject(err));
      }
    }
  }

  private getOrCreateConfigsState(orgId: string): ConfigsState {
    if (!this._configsState[orgId]) {
      this._configsState[orgId] = this.defaultConfigsState;
    }

    return this._configsState[orgId];
  }

  private get defaultConfigsState(): ConfigsState {
    return {
      data: {},
      promises: {},
      error: null,
    };
  }

  private cloneConfigsState(state: ConfigsState): ConfigsState {
    return {
      data: state.data ? { ...state.data } : state.data,
      promises: state.promises ? { ...state.promises } : {},
      error: state.error ?? null,
    };
  }
}
